import React, { useMemo } from 'react';
import { useAuth as useClerkAuth } from '@clerk/clerk-react';
import { useAuth } from '@/hooks/useAuth';
import { getChallengeButtonText } from '@/utils/challengeButtonText';
import { cn } from '@/lib/utils';
import { BaseModal } from '../Modal/BaseModal';
import { PopupHeader } from './PopupHeader';
import { DailyChallengeView } from './DailyChallengeView';
import { CompletedChallengeView } from './CompletedChallengeView';
import { H2HChallengeView } from './H2HChallengeView';
import { PlayButton } from './PlayButton';
import { ViewArchivesLink } from './ViewArchivesLink';
import { LoadingState } from './LoadingState';
import { ErrorState } from './ErrorState';
import { lighten, darken, transparentize } from 'polished';
import type { GameChallengePopupProps, PopupTheme } from './types';
import styles from './GameChallengePopup.module.css';

export function GameChallengePopup({
  game,
  challenge,
  playerStats,
  progress,
  isCurrentPeriod,
  isLoading = false,
  error = null,
  onClose,
  onPlay,
  onRetry,
  onArchivesClick,
}: GameChallengePopupProps) {
  // IS-INV-034: Frontend MUST NOT independently compute access decisions.
  // Consume displayState from backend response only — no client-side feature checks.
  const { isSignedIn } = useClerkAuth();
  const { openAuthModal } = useAuth();

  // Check if challenge is completed
  const isCompleted = progress?.activityState === 'completed';

  // Determine if user can play this challenge.
  // Backend (gameAccessService) is the sole authority — trust displayState.
  const canPlay = challenge.displayState === 'playable';
  const needsAuth = !isSignedIn && isCurrentPeriod;
  
  // Handle play button click
  const handlePlayClick = () => {
    if (canPlay) {
      onPlay(); // anonymous users with canPlay: true go straight through
    } else if (!isSignedIn) {
      // Not playable and not signed in — auth might unlock it
      sessionStorage.setItem('intendedGame', game.id);
      sessionStorage.setItem('intendedChallenge', challenge.id.toString());
      onClose();
      openAuthModal('sign-in');
    } else {
      // TODO: Show premium upgrade modal
    }
  };
  
  // Apply signature color theming
  const theme = useMemo<PopupTheme>(() => ({
    '--signature-color': game.signatureColor,
    '--signature-color-light': lighten(0.2, game.signatureColor),
    '--signature-color-dark': darken(0.2, game.signatureColor),
    '--signature-color-soft': transparentize(0.9, game.signatureColor),
  }), [game.signatureColor]);
  
  const buttonText = getChallengeButtonText({
    canPlay,
    isCompleted,
    isCurrentPeriod,
    challengeFrequency: game.challengeFrequency,
  });
  
  // Render loading state
  if (isLoading) {
    return (
      <BaseModal
        isOpen={true}
        onClose={onClose}
        title=""
        showCloseButton={true}
        size="lg"
        className={cn(
          styles.modal,
          'max-h-[calc(100dvh-9rem-env(safe-area-inset-bottom))]',
          'p-4 sm:p-6',
          'max-w-[calc(100%-1.5rem)] sm:max-w-lg'
        )}
      >
        <LoadingState />
      </BaseModal>
    );
  }
  
  // Render error state
  if (error) {
    return (
      <BaseModal
        isOpen={true}
        onClose={onClose}
        title=""
        showCloseButton={true}
        size="lg"
        className={cn(
          styles.modal,
          'max-h-[calc(100dvh-9rem-env(safe-area-inset-bottom))]',
          'p-4 sm:p-6',
          'max-w-[calc(100%-1.5rem)] sm:max-w-lg'
        )}
      >
        <ErrorState error={error} onRetry={onRetry} />
      </BaseModal>
    );
  }
  
  return (
    <BaseModal
      isOpen={true}
      onClose={onClose}
      title=""
      showCloseButton={true}
      size="lg"
      className={cn(
          styles.modal,
          'max-h-[calc(100dvh-9rem-env(safe-area-inset-bottom))]',
          'p-4 sm:p-6',
          'max-w-[calc(100%-1.5rem)] sm:max-w-lg'
        )}
    >
      <div style={theme as React.CSSProperties} className={styles.content}>
        {/* Scrollable body — header + challenge details */}
        <div className={styles.scrollBody}>
          <PopupHeader
            icon={game.iconUrl} // Will be renamed to icon in future PR
            name={game.displayName}
            tagline={game.tagline}
            signatureColor={game.signatureColor}
          />

          {/* Challenge section - only show for games that have challenges */}
          {game.challengeType !== 'auto-challenge' && (
            <div className={styles.challengeSection}>
              {/* Show completed view if challenge is completed, otherwise show regular view */}
              {isCompleted && progress ? (
                <CompletedChallengeView
                  progress={progress}
                  challenge={challenge}
                  frequency={game.challengeFrequency}
                  scoreDescriptor={game.scoreDescriptor}
                  numberOfStars={game.numberOfStars}
                  stats={playerStats}
                  signatureColor={game.signatureColor}
                />
              ) : game.challengeType === 'internal' ? (
                <DailyChallengeView
                  stats={playerStats}
                  challenge={challenge}
                  frequency={game.challengeFrequency}
                  scoreDescriptor={game.scoreDescriptor}
                  signatureColor={game.signatureColor}
                  isCurrentPeriod={isCurrentPeriod}
                />
              ) : (
                <H2HChallengeView
                  stats={playerStats}
                  challenge={challenge}
                  signatureColor={game.signatureColor}
                />
              )}
            </div>
          )}
        </div>

        {/* Pinned action footer — always visible regardless of scroll position */}
        <div className={styles.actionSection}>
          <PlayButton
            onClick={handlePlayClick}
            disabled={!canPlay && !needsAuth}
            showLock={!canPlay}
            text={buttonText}
            signatureColor={game.signatureColor}
          />

          <ViewArchivesLink gameId={game.id} signatureColor={game.signatureColor} onArchivesClick={onArchivesClick} />
        </div>
      </div>
    </BaseModal>
  );
}